import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import clsx from 'clsx';
import { ToolHeader } from '../components/ToolHeader';
import { HashRow } from '../components/HashRow';
import { CryptoPrivacyBanner } from '../components/CryptoPrivacyBanner';
import {
  HASH_ALGOS,
  HASH_TEXT_CAP,
  hashAll,
  type HashAlgo,
  type HashCharset,
  type HashOutput,
} from '../lib/hash';

const OUTPUT_OPTIONS: ReadonlyArray<{ id: HashOutput; label: string }> = [
  { id: 'hex', label: 'hex' },
  { id: 'base64', label: 'base64' },
  { id: 'base64url', label: 'base64url' },
];

const CHARSET_OPTIONS: ReadonlyArray<{ id: HashCharset; label: string }> = [
  { id: 'utf-8', label: 'UTF-8' },
  { id: 'ascii', label: 'ASCII' },
];

/**
 * `/tools/crypto/hash` — multi-algorithm text hasher.
 *
 * Why all 5 rows live and update simultaneously: people land on this
 * tool to **compare** digests against an expected value pasted from
 * elsewhere. Forcing them to flip between MD5 / SHA-256 to find the
 * one their system uses is exactly the friction this tool exists to
 * remove. Compute cost is negligible (microseconds for typical
 * inputs); only the 10 MB cap matters.
 */
export default function HashTool(): JSX.Element {
  const [text, setText] = useState<string>('');
  const [output, setOutput] = useState<HashOutput>('hex');
  const [charset, setCharset] = useState<HashCharset>('utf-8');
  const [digests, setDigests] = useState<Record<HashAlgo, string> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // Debounce 200ms — typing in a long string shouldn't fire SHA-512
  // every keystroke. Web Crypto is fast but the GC pressure adds up.
  useEffect(() => {
    if (text.length === 0) {
      setDigests(null);
      setError(null);
      return;
    }
    if (text.length > HASH_TEXT_CAP) {
      setError(`输入超过 ${(HASH_TEXT_CAP / 1024 / 1024).toFixed(0)} MB 上限，请使用 hash-file（待实现）`);
      setDigests(null);
      return;
    }
    setPending(true);
    const t = window.setTimeout(() => {
      void (async () => {
        try {
          const all = await hashAll(text, output, charset);
          setDigests(all);
          setError(null);
        } catch (e) {
          setError(e instanceof Error ? e.message : String(e));
          setDigests(null);
        } finally {
          setPending(false);
        }
      })();
    }, 200);
    return () => {
      window.clearTimeout(t);
    };
  }, [text, output, charset]);

  const hasNonAscii = charset === 'ascii' && /[^\x00-\x7f]/.test(text);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <ToolHeader
        toolId="hash"
        category="crypto"
        description="MD5 / SHA-1 / SHA-256 / SHA-384 / SHA-512 五算法并行；hex / base64 / base64url 输出可切换。"
      />

      <CryptoPrivacyBanner />

      <section className="card-pad flex flex-col gap-3">
        <ChipRow
          label="输出编码"
          options={OUTPUT_OPTIONS}
          value={output}
          onChange={setOutput}
        />
        <ChipRow
          label="字符集"
          options={CHARSET_OPTIONS}
          value={charset}
          onChange={setCharset}
        />
        {hasNonAscii && (
          <p className="inline-flex items-center gap-1 text-[12px] text-amber-600 dark:text-amber-400">
            <AlertTriangle className="h-3.5 w-3.5" />
            ASCII 模式下，非 ASCII 字符会被替换为 '?'（不阻断；仅提示）
          </p>
        )}
      </section>

      <section className="card-pad flex flex-col gap-1">
        <header className="text-[11px] uppercase tracking-wide text-ink-dim">输入文本</header>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="input min-h-[120px] resize-y py-2 font-mono text-[12.5px]"
          placeholder="贴入要哈希的文本……"
          spellCheck={false}
        />
        <p className="text-[10.5px] text-ink-dim">
          {text.length.toLocaleString()} 字符 · 上限 {(HASH_TEXT_CAP / 1024 / 1024).toFixed(0)} MB
        </p>
      </section>

      <section className="card-pad flex min-h-0 flex-col gap-1 overflow-auto">
        <header className="text-[11px] uppercase tracking-wide text-ink-dim">摘要</header>
        {error && (
          <p className="inline-flex items-center gap-1 text-[12px] text-rose-600 dark:text-rose-400">
            <AlertTriangle className="h-3.5 w-3.5" />
            {error}
          </p>
        )}
        <ul className="flex flex-col divide-y divide-line/40">
          {HASH_ALGOS.map((algo) => (
            <li key={algo}>
              <HashRow
                algo={algo}
                value={digests ? digests[algo] : null}
                pending={pending && text.length > 0}
              />
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

interface ChipRowProps<T extends string> {
  label: string;
  options: ReadonlyArray<{ id: T; label: string }>;
  value: T;
  onChange: (v: T) => void;
}

function ChipRow<T extends string>({
  label,
  options,
  value,
  onChange,
}: ChipRowProps<T>): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="w-20 text-ink-muted">{label}</span>
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          className={clsx(
            'rounded-md border px-2 py-0.5 text-[12px] transition',
            value === o.id
              ? 'border-accent bg-accent/10 text-accent'
              : 'border-line text-ink-muted hover:border-accent/60 hover:text-accent',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
